import { logWarn, logInfo } from '@/lib/logger';
/**
 * Microsoft Clarity integration
 *
 * Uses script tag injection (Microsoft's recommended approach) instead of
 * the buggy @microsoft/clarity npm package which throws "a[c] is not a function" errors.
 */

type ClarityFunction = (...args: unknown[]) => void;

interface ClarityWindow extends Window {
  clarity?: ClarityFunction & { q?: unknown[][] };
}

let isInitialized = false;
// Set once stop() ends recording; tags and events after that are dropped.
let isStopped = false;

/**
 * Check if Clarity is available on window
 */
function isClarityReady(): boolean {
  return isInitialized && !isStopped && typeof window !== 'undefined' && typeof (window as ClarityWindow).clarity === 'function';
}

/**
 * Get the clarity function from window
 */
function getClarity(): ClarityFunction | undefined {
  return (window as ClarityWindow).clarity;
}

/**
 * Initialize Microsoft Clarity via script tag injection
 * This is more reliable than the npm package
 */
export function init(projectId: string): void {
  if (isInitialized) return;
  if (typeof window === 'undefined') return;

  if (!projectId) {
    logWarn('[Clarity] No project ID provided, skipping initialization');
    return;
  }

  try {
    // Clarity initialization script (from Microsoft's setup guide)
    const w = window as ClarityWindow;
    const clarityQueue: ClarityFunction & { q?: unknown[][] } = function clarityQueue(...args: unknown[]) {
      (w.clarity!.q = w.clarity!.q || []).push(args);
    };
    w.clarity = w.clarity || clarityQueue;

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.clarity.ms/tag/' + projectId;
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript?.parentNode?.insertBefore(script, firstScript);

    isInitialized = true;
    logInfo('[Clarity] Initialized with project ID:', projectId);
  } catch (error) {
    logWarn('[Clarity] Failed to initialize:', error);
  }
}

/**
 * Stop recording for the rest of this page's lifetime.
 *
 * Clarity is a marketing-site tool only. A Clarity session replay of the
 * logged-in app would capture customer personal data rendered on screen
 * (developer names, PR titles, review comments), which is not a use our DPA
 * (docs/legal/dpa.md) discloses. So the app never identifies a user to
 * Clarity: analytics.identify() calls this instead, which ends a recording
 * that began on a public page before the visitor signed in. Safe to call
 * before the tag script has loaded - the command is queued and replayed.
 */
export function stop(): void {
  if (!isInitialized || isStopped) return;
  isStopped = true;

  try {
    getClarity()?.('stop');
  } catch (error) {
    logWarn('[Clarity] Failed to stop:', error);
  }
}

/**
 * Set a custom tag for session filtering
 */
export function setTag(key: string, value: string): void {
  if (!isClarityReady()) return;

  try {
    getClarity()?.('set', key, value);
  } catch (error) {
    logWarn('[Clarity] Failed to set tag:', error);
  }
}

/**
 * Track an event as a Clarity tag (for filtering)
 */
export function trackEvent(eventName: string, params?: Record<string, string>): void {
  if (!isClarityReady()) return;

  try {
    // Clarity doesn't have native event tracking, but we can use tags
    getClarity()?.('set', 'last_event', eventName);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        getClarity()?.('set', `event_${key}`, value);
      });
    }
  } catch (error) {
    logWarn('[Clarity] Failed to track event:', error);
  }
}
